import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtDate } from "@/lib/ui";
import { getChoices } from "@/lib/choices";
import useIsMobile from "@/lib/useIsMobile";
import useHome from "@/lib/useHome";
import { Loader2, Navigation, Car, Clock, CheckCircle2, AlertTriangle, XCircle, MapPin, Home, ListChecks } from "lucide-react";

const ASSOC_COLOR = { PRCA: "#003087", CCA: "#8C6239", CPRA: "#C8000F", APRA: "#0A7D5A" };

const PERF_HOURS = 2.5;   // assumed length of a performance you're competing in
const SPEED_KMH = 85;     // average highway driving speed incl. stops

function parseDT(date, time) {
  if (!date) return null;
  const m = /(\d{1,2}):(\d{2})\s*(AM|PM)/i.exec(time || "");
  let h = 12, min = 0;
  if (m) {
    h = parseInt(m[1], 10); min = parseInt(m[2], 10);
    const ap = m[3].toUpperCase();
    if (ap === "PM" && h !== 12) h += 12;
    if (ap === "AM" && h === 12) h = 0;
  }
  return new Date(`${date}T${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}:00`);
}

function haversineKm(a, b) {
  if (!a.lat || !b.lat) return null;
  const R = 6371, toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s)));
}

function verdict(gapHours, driveHours) {
  if (gapHours == null || driveHours == null) return null;
  const slack = gapHours - PERF_HOURS - driveHours;
  if (slack >= 2) return { key: "go", label: "Doable", color: "#3E9E4F", Icon: CheckCircle2 };
  if (slack >= 0) return { key: "tight", label: "Tight", color: "#E68A2E", Icon: AlertTriangle };
  return { key: "no", label: "Won't make it", color: "#C8000F", Icon: XCircle };
}

function fmtHrs(h) {
  if (h == null) return "—";
  const hh = Math.floor(h), mm = Math.round((h - hh) * 60);
  return hh > 0 ? `${hh}h ${mm}m` : `${mm}m`;
}

export default function Planner() {
  const m = useIsMobile();
  const nav = useNavigate();
  const home = useHome();
  const [rodeos, setRodeos] = useState(null);
  const [counts, setCounts] = useState({});   // rodeo_id -> [{event, designation, planned, ...}]
  useEffect(() => { api.get("/rodeos").then((r) => setRodeos(r.data)); }, []);
  useEffect(() => {
    api.get("/me/competitor").then((r) => {
      const nm = (r.data?.competitor_name || "").trim();
      if (!nm) return;
      api.get(`/my/rodeo-counts?name=${encodeURIComponent(nm)}`).then((res) => {
        const map = {};
        (res.data?.associations || []).forEach((a) => (a.events || []).forEach((e) =>
          (e.detail || []).forEach((d) => {
            (map[d.rodeo_id] = map[d.rodeo_id] || []).push({
              association: a.association, event: e.event, designation: d.designation,
              planned: d.planned, low_value: d.low_value, rule_type: e.rule_type,
              official: e.official, max_official: e.max_official, projected_official: e.projected_official,
              completed: e.completed, min_required: e.min_required,
            });
          })));
        setCounts(map);
      }).catch(() => {});
    }).catch(() => {});
  }, []);

  const choices = getChoices();
  const entered = [];
  (rodeos || []).forEach((r) => {
    const ch = choices[r.id];
    if (!ch) return;
    ["pref1", "pref2"].forEach((k) => {
      const p = r.performances.find((x) => x.id === ch[k]);
      if (p && p.date) entered.push({ rid: r.id, rodeo: r.name, city: r.city, state: r.state, lat: r.lat, lng: r.lng, label: p.label, date: p.date, time: p.time, dt: parseDT(p.date, p.time) });
    });
  });
  entered.sort((a, b) => (a.dt?.getTime() || 0) - (b.dt?.getTime() || 0));
  const homeStop = { rid: null, rodeo: home.town ? `Home — ${home.town}` : "Home base", city: "", state: "", lat: home.lat, lng: home.lng, label: "Start", date: null, time: null, dt: null, home: true };
  const stops = entered.length ? [homeStop, ...entered] : [];

  const plannedOfficials = stops.reduce((n, s) => n + (counts[s.rid] || []).filter((c) => c.planned && c.designation === "official" && c.rule_type === "max_official").length, 0);
  const lowValueOnTrip = stops.reduce((n, s) => n + (counts[s.rid] || []).filter((c) => c.low_value).length, 0);

  const legs = [];
  let totalKm = 0;
  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i], b = stops[i + 1];
    const km = haversineKm(a, b);
    if (km != null) totalKm += km;
    const gapHours = a.dt && b.dt ? (b.dt - a.dt) / 3600000 : null;
    const driveHours = km != null ? km / SPEED_KMH : null;
    legs.push({ km, gapHours, driveHours, v: verdict(gapHours, driveHours), sameDay: a.date && b.date && a.date === b.date });
  }

  const page = m ? "bg-[#0C1017] min-h-screen text-slate-200 pb-24" : "";
  const title = m ? "text-slate-100" : "text-[#14181F]";
  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";
  const sub = m ? "text-slate-400" : "text-slate-500";

  return (
    <div className={page} data-testid="planner-page">
      <div className={`px-4 sm:px-6 py-5 border-b flex items-center gap-2 ${m ? "border-white/10" : "border-[#E4E7EB]"}`}>
        <Navigation className="w-6 h-6 text-[#C8000F]" />
        <div>
          <h1 className={`font-display text-2xl font-extrabold tracking-tight ${title}`}>Can I Make It?</h1>
          <div className={`text-xs ${sub}`}>Your entered runs in order, with drive time between each</div>
        </div>
      </div>

      <div className="p-4 sm:p-6 max-w-3xl">
        {!rodeos ? <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>
          : stops.length < 2 ? (
            <div className={`rounded-xl border border-dashed p-10 text-center ${m ? "border-white/15 text-slate-400" : "border-[#D7DBE0] text-slate-500"}`}>
              <MapPin className="w-6 h-6 mx-auto mb-2 opacity-60" />
              <div className="text-sm">Pick performances on the Calendar, then we'll map your run from home and tell you if the timing works between rodeos.</div>
              <button onClick={() => nav("/calendar")} className="mt-4 text-sm font-semibold text-[#C8000F] hover:underline">Go to Calendar</button>
            </div>
          ) : (
            <>
              <div className={`rounded-xl border ${card} px-4 py-3 mb-4 flex items-center justify-between`} data-testid="planner-summary">
                <div className={`text-sm ${sub}`}>{stops.length - 1} performances · {legs.filter((l) => l.v?.key === "no").length} conflict(s)</div>
                <div className={`font-display font-bold ${title}`}>{totalKm.toLocaleString()} km total</div>
              </div>

              {(plannedOfficials > 0 || lowValueOnTrip > 0) && (
                <div className={`rounded-xl border ${card} px-4 py-3 mb-4 flex items-center gap-2`} data-testid="planner-counts-summary">
                  <ListChecks className="w-4 h-4 text-[#C8000F] shrink-0" />
                  <div className={`text-xs ${sub}`}>
                    {plannedOfficials > 0 && <span>This trip adds <b className={title}>{plannedOfficials}</b> planned official count{plannedOfficials > 1 ? "s" : ""} to your standings. </span>}
                    {lowValueOnTrip > 0 && <span className="text-[#B8860B]">{lowValueOnTrip} stop below your added-money threshold — review in My Rodeo Counts.</span>}
                  </div>
                </div>
              )}

              <div className="relative">
                {stops.map((s, i) => (
                  <div key={i}>
                    <button onClick={() => s.rid && nav(`/rodeo/${s.rid}`)} data-testid={`planner-stop-${i}`}
                      className={`w-full text-left rounded-xl border ${card} p-4 flex items-center gap-3 ${m || s.home ? "" : "hover:border-slate-400"}`}>
                      <div className={`w-9 h-9 rounded-full text-white font-display font-bold flex items-center justify-center shrink-0 ${s.home ? "bg-[#E68A2E]" : "bg-[#C8000F]"}`}>{s.home ? <Home className="w-4 h-4" /> : i}</div>
                      <div className="min-w-0 flex-1">
                        <div className={`font-display font-bold ${title}`}>{s.rodeo}</div>
                        <div className={`text-xs ${sub}`}>{s.home ? "Your starting point" : `${[s.city, s.state].filter(Boolean).join(", ")} · ${s.label} · ${fmtDate(s.date)}${s.time ? ` · ${s.time}` : ""}`}</div>
                      </div>
                    </button>

                    {!s.home && (counts[s.rid] || []).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-1.5 pl-12" data-testid={`planner-counts-${i}`}>
                        {counts[s.rid].map((c, ci) => {
                          const col = ASSOC_COLOR[c.association] || "#64748B";
                          let label;
                          if (c.rule_type === "max_official") {
                            const dz = c.designation === "official" ? "Official" : "Unofficial";
                            label = `${c.event} · ${dz}${c.designation === "official" ? ` ${c.projected_official}/${c.max_official}` : ""}`;
                          } else if (c.rule_type === "min_required") {
                            label = `${c.event} · ${c.completed}/${c.min_required}`;
                          } else {
                            label = c.event;
                          }
                          return (
                            <span key={ci} className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5"
                              style={{ color: col, background: col + "18", border: `1px solid ${col}33` }}>
                              {c.low_value && <AlertTriangle className="w-3 h-3 text-[#B8860B]" />}
                              {label}
                            </span>
                          );
                        })}
                      </div>
                    )}

                    {i < legs.length && (() => {
                      const leg = legs[i];
                      const VIcon = leg.v?.Icon;
                      return (
                      <div className="flex items-center gap-3 py-2 pl-4">
                        <div className="w-9 flex justify-center">
                          <div className={`w-0.5 h-full min-h-[38px] ${leg.v ? "" : "opacity-40"}`} style={{ background: leg.v?.color || "#8A94A6" }} />
                        </div>
                        <div className={`flex-1 rounded-lg border ${card} px-3 py-2 flex items-center justify-between gap-3`} data-testid={`planner-leg-${i}`}>
                          <div className={`flex items-center gap-3 text-xs ${sub} flex-wrap`}>
                            <span className="flex items-center gap-1"><Car className="w-3.5 h-3.5" /> {leg.km != null ? `${leg.km} km` : "—"}</span>
                            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {fmtHrs(leg.driveHours)} drive</span>
                            <span>{leg.sameDay ? "same day" : `${fmtHrs(leg.gapHours)} apart`}</span>
                          </div>
                          {leg.v && VIcon && (
                            <span className="flex items-center gap-1 text-xs font-bold shrink-0" style={{ color: leg.v.color }}>
                              <VIcon className="w-4 h-4" /> {leg.v.label}
                            </span>
                          )}
                        </div>
                      </div>
                      );
                    })()}
                  </div>
                ))}
              </div>

              <p className={`text-[11px] mt-4 ${sub}`}>Estimates assume ~{SPEED_KMH} km/h driving and a {PERF_HOURS}-hour performance. Distances are straight-line — real roads may be longer.</p>
            </>
          )}
      </div>
    </div>
  );
}
